import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
    BrokerPortalFeatureCodes,
    KeyValuePair,
    MailTemplate,
    ProductTag,
    RenewableSystemType
} from '@brokerportal/common/enums';
import { NewApplicationAndSendSmsLinkMessage, NewApplicationLinkMessage } from '@brokerportal/common/models';
import {
    InterestFreeApplicationDetails,
    LinkPrefillData,
    SavingFrequency
} from '@brokerportal/common/models/interest-free-application-details.model';
import { FeatureFlagService, MailService } from '@brokerportal/common/services';
import BpValidators from '@brokerportal/common/validators';
import { Scheme } from '@brokerportal/modules/quote/shared/models';
import { FullStateNames } from 'app/constants';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface InterestFreeLinkPrefillData {
    systemCost?: number;
    deposit?: number;
    clientName?: {
        firstName?: string;
        middleName?: string;
        lastName?: string;
    };
    clientEmail?: string;
    clientPhoneNumber?: string;
    clientResidentialAddress?: {
        fullAddress?: string;
        unitNumber?: string;
        streetNumber?: string;
        street?: string;
        suburb?: string;
        postcode?: string;
        state?: string;
    };
    totalIncomeLessThanThresholdConfirmed?: boolean;
}

export interface SendLinkModalData {
    productTag: ProductTag;
    partnerId: number;
    brokerId: string;
    brokerName: string;
    brokerUserName: string;
    renewableSubsidy?: string;
    renewableRebate?: string;
    additionalData?: Record<string, any>;
    prefillData?: InterestFreeLinkPrefillData;
    isSystemCostDisabled?: boolean;
}

@Component({
    selector: 'bp-interest-free-link-generator',
    templateUrl: './interest-free-link-generator.component.html',
    styleUrls: ['./interest-free-link-generator.component.scss']
})
export class InterestFreeLinkGeneratorComponent implements OnInit, OnDestroy {
    public applicationLinkForm: FormGroup;
    public isLoading: boolean = false;
    public addressStatesForBpAddress: Array<KeyValuePair<string, string>>;
    public sortedSavingFrequencies: { key: string; value: SavingFrequency }[];
    private enableSendingInterestFreeApplicationLinkViaSms: boolean = false;

    private readonly destroyed$ = new Subject<void>();

    public get formControls() {
        return this.applicationLinkForm?.controls;
    }
    public get clientNameForm(): FormGroup | null {
        return this.formControls?.clientName as FormGroup | null;
    }
    public get clientAddressForm(): FormGroup | null {
        return this.formControls?.clientResidentialAddress as FormGroup | null;
    }
    public get electricityBillSavingsForm(): FormGroup | null {
        return this.formControls?.electricityBillSavings as FormGroup | null;
    }

    public get isHeslp(): boolean {
        return (
            this.dialogInputData?.additionalData?.['extraFieldsDict.externalprogram'] === 'HESLP' ||
            this.dialogInputData?.additionalData?.externalProgram === 'HESLP'
        );
    }

    public get dialogTitle(): string {
        return this.isHeslp ? 'NSW Home Energy Solar Loan Program (HESLP)' : 'Generate Interest-Free Application Link';
    }

    constructor(
        public dialogRef: MatDialogRef<InterestFreeLinkGeneratorComponent>,
        @Inject(MAT_DIALOG_DATA) public dialogInputData: SendLinkModalData,
        private mailService: MailService,
        private snackBar: MatSnackBar,
        private fb: FormBuilder,
        private featureFlagService: FeatureFlagService
    ) {}

    ngOnInit(): void {
        this.applicationLinkForm = this.createApplicationForm();
        this.populateAddressStates();
        this.prepareSortedSavingFrequencies();
        this.featureFlagService
            .isEnabled(BrokerPortalFeatureCodes.EnableSendingInterestFreeApplicationLinkViaSms)
            .subscribe(enabled => (this.enableSendingInterestFreeApplicationLinkViaSms = enabled));

        // Pre-select NSW state for HESLP if not already set by prefill data
        if (this.isHeslp && !this.dialogInputData.prefillData?.clientResidentialAddress?.state) {
            this.clientAddressForm?.get('state')?.setValue('NSW');
        }

        if (this.dialogInputData.prefillData) {
            this.applicationLinkForm.patchValue(this.dialogInputData.prefillData);
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    public shouldShowSolarPanelSize(): boolean {
        const systemTypeValue = this.formControls?.systemType?.value;
        if (!systemTypeValue || typeof systemTypeValue !== 'string' || systemTypeValue.trim() === '') {
            return false;
        }
        const systemTypes = systemTypeValue.split(',').map(type => type.trim());
        return systemTypes.includes(RenewableSystemType.Solar);
    }

    public shouldShowBatterySize(): boolean {
        const systemTypeValue = this.formControls?.systemType?.value;
        if (!systemTypeValue || typeof systemTypeValue !== 'string' || systemTypeValue.trim() === '') {
            return false;
        }
        const systemTypes = systemTypeValue.split(',').map(type => type.trim());
        return systemTypes.includes(RenewableSystemType.Battery);
    }

    public onCancel(): void {
        this.dialogRef.close();
    }

    public onSubmit(): void {
        if (this.applicationLinkForm.invalid) {
            this.applicationLinkForm.markAllAsTouched();
            this.displaySnackbar('Please correct the errors in the form.', true);
            return;
        }

        this.isLoading = true;
        const rawFormValue: InterestFreeApplicationDetails = this.applicationLinkForm.getRawValue();
        const dataForLinkPrefill = this.mapFormToLinkData(rawFormValue);

        const linkParameters = this.mailService.getParamatersSendToEmail(
            this.dialogInputData.brokerUserName,
            this.dialogInputData.productTag,
            dataForLinkPrefill,
            this.dialogInputData.additionalData
        );

        const commonMessageData: NewApplicationLinkMessage = {
            body: 'Here is your pre-filled application link. Please click the link to complete your application.',
            subject: 'Application Link',
            to: rawFormValue.clientEmail,
            brokerId: this.dialogInputData.brokerId,
            brokerUserName: this.dialogInputData.brokerUserName,
            productTag: this.dialogInputData.productTag,
            parameters: linkParameters
        };

        const messageData = this.enableSendingInterestFreeApplicationLinkViaSms
            ? ({
                  ...commonMessageData,
                  phoneNumber: rawFormValue.clientPhoneNumber,
                  shouldSendSms: true
              } as NewApplicationAndSendSmsLinkMessage)
            : commonMessageData;

        const mailTemplateToSend = MailTemplate.StartNewApplication;

        this.mailService
            .send(mailTemplateToSend, messageData)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: () => {
                    this.isLoading = false;
                    this.displaySuccessMessage(messageData.to);
                    this.dialogRef.close({ success: true, emailSentTo: messageData.to });
                },
                error: errorResponse => {
                    this.isLoading = false;
                    const errorMessage =
                        errorResponse?.error?.message ||
                        errorResponse?.message ||
                        'An unexpected error occurred. Please try again.';
                    this.displaySnackbar(`Failed to send link: ${errorMessage}`, true);
                }
            });
    }

    private createApplicationForm(): FormGroup {
        const systemCostValidators = [Validators.required, Validators.min(1001), Validators.pattern(/^\d+$/)];
        if (this.isHeslp) {
            systemCostValidators.push(Validators.max(15000));
        }

        return this.fb.group({
            systemCost: [
                { value: null, disabled: this.dialogInputData.isSystemCostDisabled ?? false },
                systemCostValidators
            ],
            clientName: this.fb.group({
                firstName: ['', [Validators.required, Validators.maxLength(100)]],
                middleName: ['', Validators.maxLength(100)],
                lastName: ['', [Validators.required, Validators.maxLength(100)]]
            }),
            clientEmail: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
            clientPhoneNumber: [
                '',
                [
                    Validators.required,
                    Validators.pattern(/^(\+?\(61\)|\(\+?61\)|\+?61|\(0[1-9]\)|0[1-9])?( ?-?[0-9]){7,9}$/),
                    Validators.maxLength(20)
                ]
            ],
            clientResidentialAddress: this.fb.group(
                {
                    fullAddress: [null],
                    unitNumber: [null],
                    streetNumber: [null, Validators.required],
                    street: [null, [Validators.required, Validators.maxLength(100)]],
                    suburb: [null, [Validators.required, Validators.maxLength(100)]],
                    postcode: [null, [Validators.required, Validators.pattern(/^\d{4}$/)]],
                    state: [null, [Validators.required, this.heslpNswOnlyValidator.bind(this)]]
                },
                { validators: this.addressRequiredValidator() }
            ),
            systemType: [null, [Validators.required, this.heslpSystemTypeValidator.bind(this)]],
            deposit: [null],
            solarPanelSize: [
                null,
                [
                    BpValidators.conditionallyDriven(
                        'systemType',
                        (self, driver) => driver && driver.value?.split(',').includes(RenewableSystemType.Solar),
                        [Validators.required, Validators.min(0.1), Validators.pattern(/^\d+(\.\d{1,2})?$/)]
                    )
                ]
            ],
            batterySize: [
                null,
                [
                    BpValidators.conditionallyDriven(
                        'systemType',
                        (self, driver) => driver && driver.value?.split(',').includes(RenewableSystemType.Battery),
                        [Validators.required, Validators.min(0.1), Validators.pattern(/^\d+(\.\d{1,2})?$/)]
                    )
                ]
            ],
            electricityBillSavings: this.fb.group({
                amount: [null, [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
                frequency: [null, Validators.required],
                pricingExplanationConfirmation: [null, Validators.requiredTrue]
            }),
            ...(this.dialogInputData.renewableRebate === Scheme.WesternAustralia || this.isHeslp
                ? { totalIncomeLessThanThresholdConfirmed: [null, Validators.requiredTrue] }
                : {})
        });
    }

    private heslpSystemTypeValidator(control: AbstractControl): ValidationErrors | null {
        if (!this.isHeslp) {
            return null;
        }

        const systemTypeValue = control.value;

        // Treat whitespace-only values as invalid (empty)
        if (systemTypeValue && typeof systemTypeValue === 'string' && systemTypeValue.trim() === '') {
            return { required: true };
        }

        if (!systemTypeValue || typeof systemTypeValue !== 'string') {
            return null;
        }

        const systemTypes = systemTypeValue.split(',').map(type => type.trim());
        const hasSolar = systemTypes.includes(RenewableSystemType.Solar);
        const hasBattery = systemTypes.includes(RenewableSystemType.Battery);
        const hasHeatPump = systemTypes.includes(RenewableSystemType.HeatPump);

        // HESLP requires battery or heat pump
        if (hasSolar && !hasBattery && !hasHeatPump) {
            return { heslpSolarOnly: true };
        }

        // Solar only is not allowed
        if (systemTypes.length === 1 && hasSolar) {
            return { heslpSolarOnly: true };
        }

        return null;
    }

    private heslpNswOnlyValidator(control: AbstractControl): ValidationErrors | null {
        if (!this.isHeslp) {
            return null;
        }

        const stateValue = control.value;

        // If there's a value and it's not NSW, reject it
        if (stateValue && stateValue !== 'NSW') {
            return { heslpNswOnly: true };
        }

        return null;
    }

    private addressRequiredValidator(): (group: AbstractControl) => ValidationErrors | null {
        return (group: AbstractControl): ValidationErrors | null => {
            const fullAddressControl = group.get('fullAddress');
            const streetNumberControl = group.get('streetNumber');
            const streetControl = group.get('street');
            const suburbControl = group.get('suburb');
            const postcodeControl = group.get('postcode');
            const stateControl = group.get('state');

            const isManualAddressSufficient =
                streetNumberControl?.valid &&
                streetControl?.valid &&
                suburbControl?.valid &&
                postcodeControl?.valid &&
                stateControl?.valid;

            if (fullAddressControl?.errors?.['selectedAddressRequired'] && !isManualAddressSufficient) {
                return { addressSelectionOrManualRequired: true };
            }
            if (!isManualAddressSufficient) {
                return { addressIncomplete: true };
            }
            return null;
        };
    }

    private populateAddressStates(): void {
        const allStates = (Object.keys(FullStateNames) as Array<keyof typeof FullStateNames>).map(key => ({
            key: key,
            value: FullStateNames[key]
        }));

        if (this.isHeslp) {
            this.addressStatesForBpAddress = allStates.filter(state => state.key === 'NSW');
        } else {
            this.addressStatesForBpAddress = allStates;
        }
    }

    private prepareSortedSavingFrequencies(): void {
        const desiredOrder: SavingFrequency[] = [
            SavingFrequency.Quarterly,
            SavingFrequency.Monthly,
            SavingFrequency.Annually
        ];
        this.sortedSavingFrequencies = desiredOrder.map(value => ({ key: value, value: value }));
    }

    private mapFormToLinkData(formData: InterestFreeApplicationDetails): LinkPrefillData {
        const { clientName, clientResidentialAddress, electricityBillSavings } = formData;

        const linkData: LinkPrefillData = {
            clientEmail: formData.clientEmail,
            clientPhoneNumber: formData.clientPhoneNumber,
            systemCost: formData.systemCost,
            deposit: formData.deposit,
            systemType: formData.systemType,
            estimatedElectricityBillSavings: electricityBillSavings.amount,
            electricityBillSavingsFrequency: electricityBillSavings.frequency,
            pricingExplanationConfirmation: electricityBillSavings.pricingExplanationConfirmation,

            applicantFirstName: clientName.firstName,
            applicantLastName: clientName.lastName,
            applicantMiddleName: clientName.middleName || undefined,

            residentialUnitNumber: clientResidentialAddress.unitNumber,
            residentialStreetNumber: clientResidentialAddress.streetNumber,
            residentialStreetName: clientResidentialAddress.street,
            residentialSuburb: clientResidentialAddress.suburb,
            residentialPostcode: clientResidentialAddress.postcode,
            residentialState: clientResidentialAddress.state,

            brokerEmail: this.dialogInputData.brokerUserName
        };

        if (formData.solarPanelSize) {
            linkData.solarPanelSize = formData.solarPanelSize.toString();
        }
        if (formData.batterySize) {
            linkData.batterySize = formData.batterySize;
            linkData.batterySizeIndividual = formData.batterySize.toString();
        }
        if (clientResidentialAddress.fullAddress) {
            linkData.residentialFullAddress = clientResidentialAddress.fullAddress;
        }
        if (this.dialogInputData.renewableSubsidy) {
            linkData.renewableSubsidyApplication = this.dialogInputData.renewableSubsidy;
        }
        if (this.dialogInputData.renewableRebate) {
            linkData.renewableRebateApplication = this.dialogInputData.renewableRebate;
        }
        if (formData.totalIncomeLessThanThresholdConfirmed !== undefined) {
            linkData.totalIncomeLessThanThresholdConfirmed = formData.totalIncomeLessThanThresholdConfirmed;
        }

        return linkData;
    }

    private displaySuccessMessage(emailAddress: string): void {
        this.displaySnackbar(`Application link emailed successfully to ${emailAddress}`);
    }

    private displaySnackbar(message: string, isError: boolean = false): void {
        this.snackBar.open(message, 'CLOSE', {
            duration: isError ? 7000 : 5000,
            panelClass: isError ? 'error-snackbar' : 'success-snackbar',
            horizontalPosition: 'center',
            verticalPosition: 'top'
        });
    }
}
